import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationStateService } from '@core/services/navigation-state.service';
import { ThemeService } from '@core/services/theme.service';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button/button.component';
import { Icon } from '@shared/components/icon/icon.enum';

@Component({
  selector: 'app-navigation',
  imports: [ButtonComponent, TranslateModule, AsyncPipe],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ width: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ width: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ width: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  // Constants for scroll-based navigation
  private readonly DOM_READY_DELAY_MS = 300;
  private readonly SCROLL_BOTTOM_THRESHOLD_PX = 100;
  private readonly SCROLL_CONTAINER_SELECTOR = '.overflow-y-auto';
  private readonly SURVEY_FORM_SELECTOR = 'app-survey-form';
  private readonly QUESTION_SELECTOR = '[data-question-id]';

  @Input() hasPreviousPage?: boolean;
  @Input() disableNext?: boolean;
  @Input() disablePrevious?: boolean;
  @Input() customHostClasses?: string = '';
  @Input() nextButtonText?: string;
  @Input() validationErrorCount?: number;
  @Output() nextClick = new EventEmitter<Event>();
  @Output() previousClick = new EventEmitter<Event>();
  icon: Icon = Icon.Chevron;

  // Mobile keyboard detection
  isKeyboardVisible = false;
  isMobile = false;
  initialViewportHeight = 0;
  currentViewportHeight = 0;
  private focusHandler: ((event: FocusEvent) => void) | null = null;
  private focusOutHandler: ((event: FocusEvent) => void) | null = null;
  private touchHandler: ((event: TouchEvent) => void) | null = null;
  private scrollHandler: (() => void) | null = null;
  private viewportResizeHandler: (() => void) | null = null;
  private lastFocusTime = 0;

  // Scroll-based navigation visibility
  private mutationObserver?: MutationObserver;
  private hideUntilEndOfPage = false;
  private hasReachedBottom = false;
  private bottomScrollHandler: (() => void) | null = null;
  private lastQuestionIds: string[] = [];

  constructor(
    public navigationStateService: NavigationStateService,
    private elementRef: ElementRef,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.setupKeyboardDetection();
    this.setupScrollBasedNavigation();
  }

  ngOnDestroy(): void {
    this.cleanupKeyboardDetection();
    this.mutationObserver?.disconnect();

    if (this.bottomScrollHandler) {
      const scrollContainer = document.querySelector(this.SCROLL_CONTAINER_SELECTOR);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', this.bottomScrollHandler);
      }
    }
  }

  /**
   * Checks if an element is a text input that triggers a keyboard.
   */
  private isTextInput(el: HTMLElement | null): boolean {
    if (!el) return false;
    if (el.tagName === 'TEXTAREA') return true;
    if (el.isContentEditable) return true;
    if (el.tagName === 'INPUT') {
      const type = (el as HTMLInputElement).type?.toLowerCase();
      // Only these input types trigger a keyboard
      const textTypes = ['text', 'email', 'number', 'tel', 'url', 'search', 'password'];
      return textTypes.includes(type) || !type; // default type is text
    }
    return false;
  }


  private setupKeyboardDetection(): void {
    // Only enable on mobile/touch devices
    this.isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (!this.isMobile) {
      return;
    }

    this.initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    this.currentViewportHeight = this.initialViewportHeight;

    this.focusHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (this.isTextInput(target)) {
        this.lastFocusTime = Date.now();
        this.isKeyboardVisible = true;
      }
    };

    this.focusOutHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (!this.isTextInput(target)) {
        return;
      }
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement | null;
        if (!this.isTextInput(activeElement)) {
          this.isKeyboardVisible = false;
        }
      }, 0);
    };

    this.touchHandler = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (!this.isTextInput(target)) {
        this.isKeyboardVisible = false;
      }
    };

    this.scrollHandler = () => {
      const timeSinceFocus = Date.now() - this.lastFocusTime;
      if (this.isKeyboardVisible && timeSinceFocus > 500) {
        this.isKeyboardVisible = false;
      }
    };

    this.viewportResizeHandler = () => {
      this.currentViewportHeight = window.visualViewport?.height || window.innerHeight;

      const heightDifference = this.initialViewportHeight - this.currentViewportHeight;

      if (heightDifference > 150) {
        this.isKeyboardVisible = true;
      } else if (heightDifference < 50) {
        this.isKeyboardVisible = false;
      }
    };

    document.addEventListener('focusin', this.focusHandler);
    document.addEventListener('focusout', this.focusOutHandler);
    document.addEventListener('touchstart', this.touchHandler);
    // Listen for scroll on the main scrollable container
    const scrollContainer = document.querySelector('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', this.scrollHandler);
    }

    // Listen for viewport resize to track height changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.viewportResizeHandler);
    }
    window.addEventListener('resize', this.viewportResizeHandler);
  }

  private cleanupKeyboardDetection(): void {
    if (this.focusHandler) {
      document.removeEventListener('focusin', this.focusHandler);
    }
    if (this.focusOutHandler) {
      document.removeEventListener('focusout', this.focusOutHandler);
    }
    if (this.touchHandler) {
      document.removeEventListener('touchstart', this.touchHandler);
    }
    if (this.scrollHandler) {
      const scrollContainer = document.querySelector('.overflow-y-auto');
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', this.scrollHandler);
      }
    }
    if (this.viewportResizeHandler) {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
      }
      window.removeEventListener('resize', this.viewportResizeHandler);
    }
  }

  /**
   * Sets up scroll-based navigation visibility
   * Feature is enabled by default, but backend can disable via hideUntilEndOfPage: 'false'
   * Navigation is hidden initially and appears when user scrolls to bottom
   */
  private setupScrollBasedNavigation(): void {
    this.themeService.getTheme().subscribe((theme) => {
      // Feature enabled by default - only disabled if backend explicitly sends 'false'
      const hideUntilEndOfPage = theme?.parameters?.navigationFooter?.hideUntilEndOfPage;

      // Allow explicit opt-out via 'false', otherwise default to enabled
      const shouldHide = hideUntilEndOfPage !== 'false';

      if (shouldHide) {
        this.hideUntilEndOfPage = true;
        this.initializeLastQuestionObserver();
      } else {
        this.showNavigation();
      }
    });
  }

  private initializeLastQuestionObserver(): void {
    setTimeout(() => {
      const surveyForm = document.querySelector(this.SURVEY_FORM_SELECTOR);
      if (!surveyForm) {
        return;
      }

      this.mutationObserver = new MutationObserver(() => {
        this.setupBottomScrollDetection();
      });

      this.mutationObserver.observe(surveyForm, {
        childList: true,
        subtree: true,
      });

      this.setupBottomScrollDetection();
    }, this.DOM_READY_DELAY_MS);
  }

  /**
   * Sets up scroll detection to show navigation when user reaches bottom of page
   * Called initially and whenever questions change (page navigation)
   * Once navigation is shown for a given set of questions, it stays visible
   * Only resets when questions actually change (page navigation)
   */
  private setupBottomScrollDetection(): void {
    const surveyForm = document.querySelector(this.SURVEY_FORM_SELECTOR);
    if (!surveyForm) return;

    const questions = surveyForm.querySelectorAll(this.QUESTION_SELECTOR);
    if (!questions || questions.length === 0) {
      this.showNavigation();
      return;
    }

    const currentQuestionIds = Array.from(questions)
      .map((q) => q.getAttribute('data-question-id'))
      .filter((id): id is string => id !== null);

    const questionsChanged =
      currentQuestionIds.length !== this.lastQuestionIds.length ||
      currentQuestionIds.some((id, index) => id !== this.lastQuestionIds[index]);

    if (!questionsChanged && this.hasReachedBottom) {
      return;
    }

    this.lastQuestionIds = currentQuestionIds;

    this.hasReachedBottom = false;

    const scrollContainer = document.querySelector(this.SCROLL_CONTAINER_SELECTOR) as HTMLElement;

    if (!scrollContainer) {
      this.showNavigation();
      return;
    }

    const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight;

    if (!isScrollable) {
      this.showNavigation();
      return;
    }

    this.hideNavigation();
    this.attachBottomScrollHandler(scrollContainer);
  }

  private attachBottomScrollHandler(scrollContainer: HTMLElement): void {
    if (this.bottomScrollHandler) {
      scrollContainer.removeEventListener('scroll', this.bottomScrollHandler);
    }

    this.bottomScrollHandler = () => {
      if (this.hasReachedBottom) {
        return;
      }

      if (this.isAtBottomOfPage(scrollContainer)) {
        this.hasReachedBottom = true;
        this.showNavigation();
      }
    };

    scrollContainer.addEventListener('scroll', this.bottomScrollHandler);

    this.bottomScrollHandler();
  }

  private isAtBottomOfPage(scrollContainer: HTMLElement): boolean {
    const scrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    return distanceFromBottom <= this.SCROLL_BOTTOM_THRESHOLD_PX;
  }

  @HostBinding('class') get hostClasses() {
    return this.customHostClasses;
  }
  handleNextClick() {
    if (this.disableNext) return;

    this.nextClick?.emit();
  }
  handlePreviousClick(event: Event) {
    if (this.disablePrevious) return;

    this.previousClick?.emit(event);
  }

  goToValidationClick() {
    const validator = document.querySelector('.error-selector');
    validator?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }

  private showNavigation(): void {
    const navElement = this.elementRef.nativeElement;
    navElement.classList.remove('nav-hidden');
    navElement.classList.add('nav-visible');
  }

  private hideNavigation(): void {
    const navElement = this.elementRef.nativeElement;
    navElement.classList.remove('nav-visible');
    navElement.classList.add('nav-hidden');
  }
}
